import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, MessageSquare, Linkedin, Check } from 'lucide-react';
import { Link } from 'react-scroll';
import { motion } from 'framer-motion';
import { PLANS, DELIVERY_TIMES, LANGUAGES } from '../constants';
import Layout from '../components/Layout';
import Testimonials from '../components/Testimonials';
import OrderStats from '../components/OrderStats';
import FAQ from '../components/FAQ';
import ToggleButton from '../components/ToggleButton';
import { calculatePrice } from '../utils/pricing';

export default function HomePage() {
  const navigate = useNavigate();
  const [isExperienced, setIsExperienced] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('basic');
  const [selectedLanguage, setSelectedLanguage] = useState('englishOnly');
  const [selectedDeliveryTime, setSelectedDeliveryTime] = useState(3);
  const optionsRef = useRef<HTMLDivElement>(null);

  const currentPrice = useMemo(() => {
    return calculatePrice({
      plan: selectedPlan,
      isExperienced,
      language: selectedLanguage,
      deliveryTime: selectedDeliveryTime
    });
  }, [selectedPlan, isExperienced, selectedLanguage, selectedDeliveryTime]);

  const handlePlanSelection = (plan: string) => {
    setSelectedPlan(plan);
    if (optionsRef.current) {
      optionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const calculateCardPrice = (planKey: string) => {
    const planData = PLANS[planKey as keyof typeof PLANS];
    const basePrice = !isExperienced 
      ? planData.basePrice.freshGraduate.singleLanguage
      : planData.basePrice.experienced.singleLanguage;
    return Math.round(basePrice * 0.7); // Apply 30% discount
  };

  const handleCheckout = () => {
    const params = new URLSearchParams({
      plan: selectedPlan,
      isExperienced: String(isExperienced),
      language: selectedLanguage,
      deliveryTime: String(selectedDeliveryTime)
    });

    // Navigate to checkout and scroll to top
    navigate(`/checkout?${params.toString()}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Ensure scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Layout>
      {/* Hero Section */}
      <section id="home" className="pt-32 pb-24 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">
              احصل على سيرة ذاتية احترافية تضمن لك الوظيفة
            </h1>
            <p className="text-xl text-gray-600 mb-12">
              نساعدك في كتابة سيرة ذاتية احترافية تبرز مهاراتك وخبراتك بشكل مميز
            </p>
            <div className="flex justify-center gap-4">
              <Link
                to="pricing"
                spy={true}
                smooth={true}
                offset={-100}
                className="px-8 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors cursor-pointer"
              >
                اطلب الآن
              </Link>
              <a
                href="https://wa.me/966503083277"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3 border-2 border-green-600 text-green-600 rounded-full hover:bg-green-50 transition-colors"
              >
                تواصل معنا
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">خدماتنا</h2>
            <p className="text-xl text-gray-600">نقدم مجموعة متكاملة من الخدمات لتعزيز فرصك في سوق العمل</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">سيرة ذاتية احترافية</h3>
              <p className="text-gray-600">نكتب لك سيرة ذاتية احترافية تبرز مهاراتك وخبراتك بشكل مميز</p>
            </div>
            
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">خطاب تقديمي</h3>
              <p className="text-gray-600">نكتب لك خطاب تقديمي مقنع يزيد من فرصك في الحصول على المقابلة</p>
            </div>
            
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <Linkedin className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">تحسين ملف LinkedIn</h3>
              <p className="text-gray-600">نحسن ملفك على LinkedIn لزيادة ظهورك في نتائج البحث</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">باقاتنا</h2>
            <div className="inline-block bg-red-100 text-red-600 px-4 py-2 rounded-full font-bold text-lg">
              خصم 30% لفترة محدودة
            </div>
          </div>

          <div className="flex justify-center items-center gap-4 mb-12">
            <ToggleButton
              isToggled={isExperienced}
              onToggle={() => setIsExperienced(!isExperienced)}
              leftLabel="حديث التخرج"
              rightLabel="ذو خبرة"
            />
          </div>

          <OrderStats isExperienced={isExperienced} />

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {Object.entries(PLANS).map(([key, plan]) => {
              const basePrice = !isExperienced 
                ? plan.basePrice.freshGraduate.singleLanguage
                : plan.basePrice.experienced.singleLanguage;
              const discountedPrice = calculateCardPrice(key);
              const savings = basePrice - discountedPrice;
              
              return (
                <motion.div 
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className={`relative bg-white p-8 rounded-2xl ${
                    selectedPlan === key ? 'border-2 border-green-600 shadow-lg' : 'border border-gray-200'
                  }`}
                >
                  <div className="absolute -top-3 right-4 bg-red-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                    وفر {savings} ريال
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-4">{plan.title}</h3>
                  
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl font-bold text-green-600">
                      {discountedPrice} ريال
                    </span>
                    <span className="text-lg text-gray-400 line-through">
                      {basePrice} ريال
                    </span>
                  </div>
                  
                  <div className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-semibold mb-6">
                    خصم 30% - العرض لفترة محدودة!
                  </div>

                  <ul className="space-y-4 mb-8 divide-y divide-gray-100">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 pt-4 first:pt-0">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handlePlanSelection(key)}
                    className={`w-full py-3 rounded-full transition-colors ${
                      selectedPlan === key
                        ? 'bg-green-600 text-white'
                        : 'bg-white text-green-600 border-2 border-green-600 hover:bg-green-50'
                    }`}
                  >
                    اختر الباقة
                  </button>
                </motion.div>
              );
            })}
          </div>

          {/* Options Section */}
          <div ref={optionsRef} className="bg-white p-8 rounded-2xl border border-gray-200 mb-12">
            <h3 className="text-2xl font-bold mb-6">خيارات إضافية</h3>
            
            <div className="space-y-8">
              <div>
                <h4 className="font-semibold mb-4">اللغة:</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.id}
                      onClick={() => setSelectedLanguage(lang.id)}
                      className={`p-4 rounded-xl border-2 transition-colors ${
                        selectedLanguage === lang.id
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-green-200'
                      }`}
                    >
                      <div className="font-semibold">{lang.label}</div>
                      {lang.id === 'both' && (
                        <div className="text-sm text-green-600">+40 ريال</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-4">وقت التسليم:</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {DELIVERY_TIMES.map((time) => (
                    <button
                      key={time.days}
                      onClick={() => setSelectedDeliveryTime(time.days)}
                      className={`p-4 rounded-xl border-2 transition-colors ${
                        selectedDeliveryTime === time.days
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-green-200'
                      }`}
                    >
                      <div className="font-semibold">{time.label}</div>
                      {time.price > 0 && (
                        <div className="text-sm text-green-600">+{time.price} ريال</div>
                      )}
                      {time.price < 0 && (
                        <div className="text-sm text-green-600">{time.price} ريال</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-12 text-center">
                <div className="price-container">
                  <div className="total-price-label">السعر الإجمالي</div>
                  <div className="price-amount">{currentPrice} ريال</div>
                  <div className="vat-text">شامل ضريبة القيمة المضافة</div>
                </div>
                
                <button
                  onClick={handleCheckout}
                  className="checkout-button"
                >
                  إتمام الطلب
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ />
    </Layout>
  );
}